import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';
import JsonLd from '../../components/feature/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

const philosophyJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/philosophy#webpage`,
      url: `${siteUrl}/philosophy`,
      name: '風土音楽の再構築と身体操法 | 日本音楽藝術表現研究所',
      description:
        '日本音楽藝術表現研究所の研究核心。風土に根ざした身体知の分析から、労働動作・倍音・創造的再構築の三軸で消えゆく郷土の知恵を現代の演奏技術へ昇華します。',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#organization` },
      inLanguage: 'ja',
    },
    {
      '@type': 'ResearchProject',
      '@id': `${siteUrl}/philosophy#research`,
      name: '風土音楽の再構築と身体操法',
      description:
        '島国の感性を基盤に、日本音楽の身体知を分析・再構築する研究プロジェクト。生活動作の抽出（操体分析）、音色の考古学（口唱歌と倍音）、現代への実装（創造的再構築）の三軸から構成。',
      url: `${siteUrl}/philosophy`,
      parentOrganization: { '@id': `${siteUrl}/#organization` },
    },
  ],
};

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, visible } = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const pillars = [
  {
    number: '01',
    title: '生活動作の抽出（操体分析）',
    body: '鍬を振る、網を引くといったような全身連動の動きを、現代の解剖学的視点から再定義し、その身体に宿るグルーヴを再定義します。',
    icon: 'ri-body-scan-line',
  },
  {
    number: '02',
    title: '音色の考古学（口唱歌と倍音）',
    body: '日本語の母音・子音の構造、および山や海の声に共鳴する特殊発声とその音を研究。倍音構造の音楽からその島国の「気配」を呼び覚ます音色を引き出します。',
    icon: 'ri-sound-module-line',
  },
  {
    number: '03',
    title: '現代への実装（創造的再構築）',
    body: '日本音楽のコンセプトを、現代のアートやエンターテインメント、教育の場へと接続します。それは過去の保存にとどまらず、失われつつある「島国の感性」を、現代社会を生き抜くための新たな「身体知」として提示する試みです。',
    icon: 'ri-links-line',
  },
];

export default function PhilosophyPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="bg-washi min-h-screen">
      <title>風土音楽の再構築と身体操法 | 日本音楽藝術表現研究所</title>
      <meta
        name="description"
        content="日本音楽藝術表現研究所の研究核心。風土に根ざした身体知の分析から、労働動作・倍音・創造的再構築の三軸で消えゆく郷土の知恵を現代の演奏技術へ昇華します。"
      />
      <meta name="keywords" content="風土音楽,身体操法,日本音楽,倍音,口唱歌,操体分析" />
      <link rel="canonical" href={`${siteUrl}/philosophy`} />
      <meta property="og:url" content={`${siteUrl}/philosophy`} />
      <meta property="og:title" content="風土音楽の再構築と身体操法 | 日本音楽藝術表現研究所" />
      <meta
        property="og:description"
        content="日本音楽藝術表現研究所の研究核心。風土に根ざした身体知の分析から、労働動作・倍音・創造的再構築の三軸で消えゆく郷土の知恵を現代の演奏技術へ昇華します。"
      />
      <meta property="og:type" content="website" />
      <JsonLd data={philosophyJsonLd} id="philosophy" />

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-white' : 'bg-transparent'
        }`}
      >
        <div className="px-4 md:px-8 pt-4 pb-2 md:pt-5 md:pb-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/79998c31-8dcb-4257-9950-200bb8348595.png"
              alt="Institute Logo"
              className="h-9 w-9 md:h-10 md:w-10 object-contain shrink-0"
            />
            <span className={`font-serif text-xs md:text-sm transition-colors whitespace-nowrap ${scrolled ? 'text-sumi' : 'text-white'}`}>
              日本音楽藝術表現研究所
            </span>
          </Link>
          <Link
            to="/"
            className={`font-sans text-xs tracking-widest transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 ${scrolled ? 'text-sumi/70 hover:text-sumi' : 'text-white/80 hover:text-white'}`}
          >
            <i className="ri-arrow-left-line text-sm"></i>
            <span>TOP</span>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/5160037d32dfa5b35795c2d0440d0d3c.jpg"
          alt="風土と身体知"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sumi/70 via-sumi/45 to-washi/95"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p className="font-sans text-[10px] tracking-[0.4em] text-white/60 mb-5 uppercase">Research Core</p>
          <h1 className="font-serif text-[1.75rem] sm:text-4xl md:text-5xl font-bold text-white leading-[1.55] mb-5">
            風土音楽の再構築と
            <span className="block mt-1">身体操法</span>
          </h1>
          <div className="w-8 h-px bg-white/40 mx-auto mb-5"></div>
          <p className="font-serif text-xs sm:text-sm text-white/60 tracking-[0.2em]">
            研究所の核心
          </p>
        </div>
      </header>

      {/* Section 1: 研究の土台 */}
      <section className="py-16 sm:py-28 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <FadeIn>
            <div className="relative pl-5 mb-12">
              <div className="absolute left-0 top-0 bottom-0 w-px bg-indigo"></div>
              <p className="font-sans text-[10px] tracking-[0.35em] text-indigo/80 mb-3 uppercase">Foundation</p>
              <h2 className="font-serif text-xl sm:text-3xl md:text-4xl font-bold text-sumi leading-[1.6]">
                研究の土台
                <span className="block text-base sm:text-xl font-normal text-sumi/60 mt-2 tracking-wide">
                  ── 風土に根ざした身体知
                </span>
              </h2>
            </div>
          </FadeIn>

          <FadeIn delay={150}>
            <div className="font-serif text-sm sm:text-lg leading-[2.4] text-sumi/75 space-y-8 pl-5">
              <p>
                日本の郷土芸能や音楽は、古来より
                「鑑賞物」である前に、厳しい自然環境における
                農作業や漁業という
                <strong className="block mt-3 text-sumi font-semibold tracking-widest text-sm sm:text-base border-l-2 border-indigo/50 pl-4">
                  「生存のための律動（リズム）」
                </strong>
                から立ち上がってきました。
              </p>
              <p>
                当研究所では、日本列島の厳しい風土を
                生き抜くために培われた、
                生活に根ざした身体技法を分析しています。
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="h-px bg-sumi/10"></div>
      </div>

      {/* Section 2: 技術の昇華 */}
      <section className="py-16 sm:py-28 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <FadeIn>
            <div className="relative pl-5 mb-5">
              <div className="absolute left-0 top-0 bottom-0 w-px bg-indigo"></div>
              <p className="font-sans text-[10px] tracking-[0.35em] text-indigo/80 mb-3 uppercase">Methodology</p>
              <h2 className="font-serif text-xl sm:text-3xl md:text-4xl font-bold text-sumi leading-[1.6]">
                技術の昇華
                <span className="block text-base sm:text-xl font-normal text-sumi/60 mt-2 tracking-wide">
                  ── 労働から芸術へ
                </span>
              </h2>
            </div>
          </FadeIn>

          <FadeIn delay={100}>
            <p className="font-serif text-sm sm:text-base leading-[2.4] text-sumi/65 mb-12 pl-5">
              単なる形の模倣ではなく、以下の三点を軸に、
              消えゆく郷土の知恵を現代の演奏技術へと昇華させます。
            </p>
          </FadeIn>

          {/* Three Pillars */}
          <div className="space-y-4">
            {pillars.map((pillar, idx) => (
              <FadeIn key={pillar.number} delay={idx * 180}>
                <article className="bg-white rounded-lg p-6 sm:p-10 relative overflow-hidden group hover:bg-indigo/[0.03] transition-colors duration-500">
                  {/* Background number */}
                  <span className="absolute right-4 top-3 font-serif text-7xl sm:text-8xl font-bold text-sumi/[0.04] select-none group-hover:text-indigo/10 transition-colors duration-500">
                    {pillar.number}
                  </span>

                  <div className="relative">
                    {/* Mobile: stacked layout */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 flex items-center justify-center shrink-0">
                        <i className={`${pillar.icon} text-xl text-indigo`}></i>
                      </div>
                      <p className="font-sans text-[10px] tracking-widest text-indigo/60 uppercase">{pillar.number}</p>
                    </div>
                    <h3 className="font-serif text-base sm:text-xl font-bold text-sumi mb-4 leading-[1.65] tracking-wide">
                      {pillar.title}
                    </h3>
                    <p className="font-serif text-sm sm:text-base leading-[2.2] text-sumi/65">
                      {pillar.body}
                    </p>
                  </div>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Tagline Section */}
      <section className="py-20 sm:py-32 px-5 sm:px-8 bg-sumi overflow-hidden">
        <div className="max-w-3xl mx-auto text-center">
          <FadeIn>
            <p className="font-sans text-[10px] tracking-[0.4em] text-white/30 mb-8 uppercase">Philosophy</p>
            <div className="relative inline-block mb-10">
              <div className="absolute -inset-8 rounded-full bg-indigo/10 blur-3xl"></div>
              <blockquote className="relative font-serif text-[1.6rem] sm:text-3xl md:text-4xl font-bold text-white leading-[1.9] tracking-widest">
                「生存の律動を、
                <span className="block">現代の共鳴へ。」</span>
              </blockquote>
            </div>
          </FadeIn>
          <FadeIn delay={200}>
            <div className="w-6 h-px bg-white/20 mx-auto mb-8"></div>
            <p className="font-serif text-xs sm:text-sm leading-[2.8] text-white/55 max-w-xs sm:max-w-xl mx-auto">
              私たちの研究は、土に触れ、海を渡った先人たちの
              「生命の躍動」を音楽の響きに凝縮し、
              次世代へと繋ぐための架け橋です。
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Back to Top CTA */}
      <section className="py-14 px-5 sm:px-8 text-center">
        <FadeIn>
          <Link
            to="/"
            className="inline-flex items-center gap-3 font-sans text-xs tracking-widest text-sumi/50 hover:text-sumi transition-colors duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span>トップに戻る</span>
          </Link>
        </FadeIn>
      </section>

      <Footer />
    </div>
  );
}
